import {
  Avatar,
  AvatarFallback,
  AvatarImage
} from "@/modern/components/ui/avatar";
import { Card, CardHeader } from "@/modern/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/modern/components/ui/collapsible";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { Badge } from "@/modern/components/ui/badge";
import { useState } from "react";
import { cn } from "@/modern/utils";
import { INCENTIVE_POINT_STATUS } from "@/other/constants/IncentiveKeys";
import { pluralize } from "@/other/utils/otherUtils";

const getRankIcon = (rank) => {
  switch (rank) {
    case 1:
      return "fa-light fa-trophy";
    case 2:
      return "fa-light fa-medal";
    case 3:
      return "fa-light fa-award";
  }
}

export const LeaderboardUserCard = ({ rank, pointsData, showStatus = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Card className="border-none" variant={isOpen ? "modern" : "ghost"}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className={cn("flex flex-row items-center justify-between cursor-pointer space-y-0", isOpen && "border-b")}>
            <div className="flex items-center gap-swivu-base">
              <div className={cn("text-swivu-icon font-semibold text-muted-foreground min-w-[1.125rem] inline-flex items-center justify-center", rank <= 3 && getRankIcon(rank), rank==1 && "text-yellow-500")}>
                {rank <= 3 ? "" : `#${rank}`}
              </div>
              <Avatar className="h-10 w-10">
                <AvatarImage src={pointsData.boardUser.profileImageURL} alt={pointsData.boardUser.name} />
                <AvatarFallback>{pointsData.boardUser.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold text-foreground mb-1">{pointsData.boardUser.name}</h3>
                <p className="text-swivu-sm text-muted-foreground m-0">
                  {pointsData.pointsRecordCount} {pluralize(pointsData.pointsRecordCount, "point")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-swivu-base">
              <div className="text-right">
                <div
                  className={`text-swivu-lg font-bold ${
                    pointsData.totalPoints < 0 ? "text-red-500" : "text-foreground"
                  }`}
                >
                  {pointsData.totalPoints > 0 ? "+" : ""}
                  {pointsData.totalPoints}
                </div>
              </div>
              {isOpen ? (
                <i className="fa-regular fa-chevron-down text-swivu-icon text-muted-foreground w-4" />
              ) : (
                <i className="fa-regular fa-chevron-right text-swivu-icon text-muted-foreground w-4" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent className="flex">
          <ScrollArea className="max-h-56 w-full h-auto">
            {pointsData.points.map((point, pointIndex) => (
              <Card
                key={pointIndex}
                className="w-full flex items-center justify-between"
                variant="transparent"
                size="base"
              >
                <div className="flex-1">
                  <div className="font-semibold text-base">{point.pointRecord.taskName || "N/A"}</div>
                  <div className="flex items-center gap-swivu-base">
                    <div className="text-swivu-sm text-muted-foreground flex items-center gap-swivu-base">
                      {point.pointRecord.incentiveName}
                      {showStatus && (
                        <Badge variant={cn(point.pointRecord.status === INCENTIVE_POINT_STATUS.APPROVED && "primary-outline", point.pointRecord.status === INCENTIVE_POINT_STATUS.AWARDED && "success-outline")} size="sm">
                          {point.pointRecord.incentiveStatus}
                        </Badge>
                      )}
                      {point.pointRecord.pointTagRecord?.name && (
                        <Badge variant="secondary" size="sm">
                          <i className="fa-regular fa-tag text-swivu-xs mr-1" />
                          {point.pointRecord.pointTagRecord.name}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    title={point.pointRecord.archived ? "Archived" : ""}
                    className={`text-swivu-md font-semibold ${
                      point.pointRecord.points < 0 ? "text-destructive" : "text-foreground"
                    } ${point.pointRecord.archived ? "text-muted-foreground line-through" : ""}`}
                  >
                    {point.pointRecord.points > 0 ? "+" : ""}
                    {point.pointRecord.points}
                  </span>
                </div>
              </Card>
            ))}
          </ScrollArea>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
